// Persistent store for the quotation document's bytes, so opening the editor after a page reload —
// or after the drawer was closed and reopened — doesn't re-download the whole PDF.
//
// `documentBlobRef` in useQuotationViewer already keeps the bytes for the life of one page; this is
// the layer underneath it, and it exists because the browser's own HTTP cache cannot help: the
// backend re-signs the presigned S3 link on every request, so the URL string changes while the
// object behind it does not (verified against the dev API — two calls 3 s apart differed only in
// `X-Amz-Date` and `X-Amz-Signature`, and `X-Amz-Expires` is 600, so even a stable signature would
// only be cacheable for ten minutes).
//
// The key is therefore the S3 OBJECT KEY, not the URL — the same `fileIdentity` the orchestrator
// already derives to tell "same object, re-signed" from "a genuinely different document".
//
// That key needs no expiry and no ETag check, because the backend gives every GENERATION of a
// document its own object. Verified on a drawing processed three times: `Processed`,
// `Pre-Annotated` and `Summary` each resolved to `…/processing/<processingId>/…`, three distinct
// paths for three processings, while the uploaded `Original` kept one path — correctly, since
// re-processing never rewrites the file the user uploaded. So a stale hit is not reachable: new
// content always arrives under a new key.

const CACHE_NAME = "quotation-documents-v1"

// Cache Storage keys are Requests, so the object key is folded into a synthetic same-origin path.
// It is never fetched — only used as a lookup handle.
const KEY_PREFIX = "/__quotation-document/"

// Documents above this never enter the cache. A single outsized file would otherwise be able to
// consume the origin's whole storage budget and get every other entry evicted with it.
export const MAX_CACHEABLE_BYTES = 120 * 1024 * 1024

// How many documents to keep. The editor works on one drawing at a time, so a handful covers
// switching between a few; past that the OLDEST entry is dropped. Cache Storage enumerates keys in
// insertion order and has no "touch" operation, so this is FIFO rather than LRU — making it LRU
// would mean rewriting the whole blob on every hit, which for a 100 MB file costs more than the
// eviction it avoids.
export const MAX_CACHED_DOCUMENTS = 5

// Cache Storage is absent outside a secure context and in jsdom, and throws rather than returning
// undefined in some privacy modes — so every entry point below is best-effort.
const getCache = async (): Promise<Cache | null> => {
	try {
		if (typeof caches === "undefined") return null
		return await caches.open(CACHE_NAME)
	} catch {
		return null
	}
}

/**
 * The stable identity of the document behind a presigned URL: its S3 object key.
 *
 * The backend re-signs the link on every request, so the URL STRING changes while the object does
 * not. Reacting to the raw string makes "same object, re-signed" indistinguishable from "a
 * different document" — which silently reloaded the whole document mid-session — and it also makes
 * the cache key miss every single time. origin + pathname is the part that stays put.
 *
 * Shared by both viewers (the quotation editor and the Documents-tab preview) so one document has
 * one identity wherever it is opened.
 */
export const resolveDocumentIdentity = (fileLink: string | undefined): string | undefined => {
	if (!fileLink) return undefined
	try {
		const url = new URL(fileLink)
		return `${url.origin}${url.pathname}`
	} catch {
		// Not a parseable absolute URL (e.g. a relative path in a test mock) — fall back to the raw
		// string rather than collapsing every value onto one identity.
		return fileLink
	}
}

/**
 * Lookup handle for one document: the signed-in user, then the S3 object key.
 *
 * The user segment is what stops one person's drawing being served to the next person who signs in
 * on a shared machine (PR #1429 review). It scopes rather than deletes on logout, deliberately:
 * clearing on logout would also throw the cache away on every E2E login cycle, which is most of
 * what it exists for.
 *
 * This hides another user's document from the APP, not from the disk — anyone with devtools on that
 * machine can still read Cache Storage directly. Accepted: these files are not sensitive enough to
 * justify the eviction pass, and the reviewer offered scoping as the alternative.
 */
export const buildDocumentCacheKey = (fileIdentity: string, userScope: string): string =>
	`${KEY_PREFIX}${encodeURIComponent(userScope)}/${encodeURIComponent(fileIdentity)}`

/** The cached bytes for `cacheKey`, or null on a miss — which callers must treat as normal, since
 * the browser may evict the whole store at any time. */
export const readCachedDocument = async (cacheKey: string): Promise<Blob | null> => {
	const cache = await getCache()
	if (!cache) return null
	try {
		const response = await cache.match(cacheKey)
		if (!response) return null
		const blob = await response.blob()
		// A zero-length entry means an interrupted write; treat it as a miss so it gets replaced.
		return blob.size > 0 ? blob : null
	} catch {
		return null
	}
}

/** Store one document's bytes, dropping the oldest entries to stay within MAX_CACHED_DOCUMENTS.
 * Failures (quota, privacy mode) are swallowed: this is a cache, never a source of truth. */
export const writeCachedDocument = async (cacheKey: string, blob: Blob): Promise<void> => {
	if (blob.size === 0 || blob.size > MAX_CACHEABLE_BYTES) return
	const cache = await getCache()
	if (!cache) return
	try {
		await cache.put(cacheKey, new Response(blob))
		const keys = await cache.keys()
		// `> MAX` rather than `>=`: the entry just written is already in there.
		for (const stale of keys.slice(0, Math.max(0, keys.length - MAX_CACHED_DOCUMENTS))) {
			await cache.delete(stale)
		}
	} catch {
		// Out of quota or storage denied — the document still loads, just without being cached.
	}
}

/** How much of the document has arrived. `total` is null when the response declared no length. */
export type TDocumentDownloadProgress = { loaded: number; total: number | null }

/**
 * The response body as a Blob, reporting progress as it arrives.
 *
 * Reads the stream by hand rather than calling `response.blob()`, because a single `blob()` call
 * reports nothing until the whole file has landed — and this file can be 120 MB, which is the entire
 * wait we want to put a number on.
 *
 * `Content-Length` is a CORS-safelisted response header, so it is readable on this cross-origin S3
 * response without the bucket exposing anything. When it is absent the download still runs; the
 * caller just shows a spinner with no percentage.
 *
 * Falls back to `blob()` when no progress is wanted, or when the environment has no readable stream
 * (jsdom, older browsers) — the download must never depend on the reporting.
 */
const readBodyWithProgress = async (
	response: Response,
	onProgress?: (progress: TDocumentDownloadProgress) => void,
): Promise<Blob> => {
	if (!onProgress || !response.body) return response.blob()

	const declared = Number(response.headers.get("Content-Length"))
	const total = Number.isFinite(declared) && declared > 0 ? declared : null
	const type = response.headers.get("Content-Type") ?? "application/pdf"

	const reader = response.body.getReader()
	const chunks: Uint8Array[] = []
	let loaded = 0
	for (;;) {
		const { done, value } = await reader.read()
		if (done) break
		if (!value) continue
		chunks.push(value)
		loaded += value.byteLength
		onProgress({ loaded, total })
	}

	return new Blob(chunks as BlobPart[], { type })
}

/**
 * The document's bytes for `fileIdentity`, from the cache when it holds them and from `url`
 * otherwise. A download is stored for next time in the background, so loading is never delayed by
 * the write. Rejects only if the download itself fails, which the caller answers by handing the URL
 * straight to the viewer.
 */
export const fetchDocumentBlob = async ({
	fileIdentity,
	userScope,
	url,
	onProgress,
}: {
	fileIdentity: string | undefined
	/**
	 * Who is signed in. Caching is SKIPPED entirely when this is absent: without it we cannot tell
	 * whose document this is, and an unscoped entry is exactly what the review asked us to stop
	 * writing. The download still happens — the viewer just gets no persistence that time.
	 */
	userScope: string | undefined
	url: string
	onProgress?: (progress: TDocumentDownloadProgress) => void
}): Promise<Blob> => {
	const cacheKey = fileIdentity && userScope ? buildDocumentCacheKey(fileIdentity, userScope) : null

	if (cacheKey) {
		const cached = await readCachedDocument(cacheKey)
		// No progress is reported for a hit: nothing is downloaded, so the caller has nothing to
		// announce and should not flash an overlay.
		if (cached) return cached
	}

	const response = await fetch(url)
	if (!response.ok) throw new Error(`Document download failed: ${response.status}`)
	const blob = await readBodyWithProgress(response, onProgress)

	// Deliberately not awaited — the viewer can start parsing while this settles.
	if (cacheKey) void writeCachedDocument(cacheKey, blob)

	return blob
}
